import './pseudoTerminal.scss';
import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';
import { FiPlus, FiMinus } from 'react-icons/fi'; // Importing icons from react-icons

const PseudoTerminal = () => {
    const outputRef = useRef(null);
    const [command, setCommand] = useState('');
    const [history, setHistory] = useState([]);
    const [commandHistory, setCommandHistory] = useState([]); // State for storing command history
    const [historyIndex, setHistoryIndex] = useState(null);  // State for tracking history navigation
    const [isMinimized, setIsMinimized] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [isClosed, setIsClosed] = useState(false);
    const [fontSize, setFontSize] = useState(14); // State for font size

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!command.trim()) return;

        if (command === 'clear') {
            setHistory([]);
            setCommand('');
            return;
        }

        // if (command === 'help') {
        //     setHistory(prev => [
        //         ...prev,
        //         { type: 'output', content: 'Available commands: clear, help' }
        //     ]);
        //     setCommand('');
        //     return;
        // }

        // Append the entered command to history
        setHistory(prev => [...prev, { type: 'command', content: `$ ${command}` }]);
        setCommand('');

        // Add the command to commandHistory and reset historyIndex
        setCommandHistory(prev => [...prev, command]);
        setHistoryIndex(null);

        try {
            const res = await api.post('/api/admin/terminal/', { command });
            setHistory(prev => [...prev, { type: 'output', content: res.data.output }]);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'An error occurred.';
            setHistory(prev => [...prev, { type: 'error', content: errorMessage }]);
        }
    };

    useEffect(() => {
        // Scroll to the bottom when history updates
        if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    }, [history]);

    // Handler for Red (Close) Button
    const handleClose = () => {
        // setIsClosed(true);
    };

    // Handler for Yellow (Minimize) Button
    const handleMinimize = () => {
        setIsMinimized(prev => !prev);
    };

    // Handler for Green (Maximize/Restore) Button
    const handleMaximize = () => {
        setIsMaximized(prev => !prev);
    };

    // Handlers for Font Size Buttons
    const handleIncreaseFont = () => {
        setFontSize(prev => (prev < 24 ? prev + 2 : prev)); // Max font size 24px
    };

    const handleDecreaseFont = () => {
        setFontSize(prev => (prev > 10 ? prev - 2 : prev)); // Min font size 10px
    };

    // Handler for navigating command history
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (commandHistory.length === 0) return;

            setHistoryIndex(prev => {
                const newIndex = prev === null ? commandHistory.length - 1 : Math.max(prev - 1, 0);
                setCommand(commandHistory[newIndex]);
                return newIndex;
            });
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (commandHistory.length === 0) return;

            setHistoryIndex(prev => {
                if (prev === null) return null;

                const newIndex = prev + 1;
                if (newIndex >= commandHistory.length) {
                    setCommand('');
                    return null;
                } else {
                    setCommand(commandHistory[newIndex]);
                    return newIndex;
                }
            });
        }
    };

    if (isClosed) return null; // Do not render anything if closed

    return (
        <div
            className={`pseudoTerminal ${isMaximized ? 'maximized' : ''}`}
            style={{ fontSize: `${fontSize}px` }} // Apply dynamic font size
        >
            <div className="terminal-header">
                <div className="terminal-buttons-left">
                    <div className="terminal-button red" onClick={handleClose} aria-label="Close Terminal"></div>
                    <div className="terminal-button yellow" onClick={handleMinimize} aria-label="Minimize Terminal"></div>
                    <div className="terminal-button green" onClick={handleMaximize} aria-label="Maximize Terminal"></div>
                </div>
                <div className="terminal-buttons-right">
                    <button
                        className="font-button"
                        onClick={handleIncreaseFont}
                        aria-label="Increase Font Size"
                        type="button"
                    >
                        <FiPlus />
                    </button>
                    <button
                        className="font-button"
                        onClick={handleDecreaseFont}
                        aria-label="Decrease Font Size"
                        type="button"
                    >
                        <FiMinus />
                    </button>
                </div>
            </div>
            {!isMinimized && (
                <div className="terminal-body">
                    <div className="terminal-output" ref={outputRef}>
                        {history.map((item, index) => (
                            <div
                                key={index}
                                className={
                                    item.type === 'command' ? 'terminal-command' :
                                    item.type === 'output' ? 'terminal-text' :
                                    'terminal-error'
                                }
                            >
                                {item.content}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {!isMinimized && (
                <form onSubmit={handleSubmit} className="terminal-input-form">
                    <input
                        type="text"
                        placeholder="Enter command"
                        value={command}
                        onChange={(e) => {
                            setCommand(e.target.value);
                            setHistoryIndex(null); // Reset historyIndex on input change
                        }}
                        onKeyDown={handleKeyDown} // Attach keyDown handler
                        autoFocus
                        style={{ fontSize: `${fontSize}px` }}
                    />
                    <button type="submit" style={{ fontSize: `${fontSize}px` }}>Run</button>
                </form>
            )}
        </div>
    );
};

export default PseudoTerminal;